import type { CruiseDayType, EntityType, EventType, InteractionEvent, LoungeRound } from "../types";
import { EVENT_TYPE_CATEGORY_MAP, isCruiseDay, normalizeEntityType } from "../types";
import { isFlightActivity } from "./flightRoute";
import { dateFromDayKey, dayKeyInZone, dayKeyOf, dayRangeContains, daysBetweenKeys, formatDayKey, todayKey } from "./calendarDay";

/** A Firestore `Timestamp`-shaped value (or absent). */
type DateLike = { toDate?: () => Date } | null | undefined;

/**
 * Whether a transportation place is a flight, from its name ("Airplane",
 * "Flight home", "Delta jet"). Flights get the plane icon and ask for boarding
 * and departure times rather than a single scheduled time.
 */
export function isFlightName(name: string | null | undefined): boolean {
  return !!name && /\b(air\s*plane|aeroplane|plane|flights?|airlines?|jet(liner)?)\b/i.test(name);
}

/** A transportation place (or activity) that is a flight. */
export function isFlight(type: string | null | undefined, name: string | null | undefined): boolean {
  return type === "TRANSPORTATION" && isFlightName(name);
}

/**
 * The icon for a type. Pass the place's name too where there is one, so a
 * flight gets a plane rather than the van every other transport gets.
 */
export function entityTypeIcon(type: EntityType | string | null | undefined, name?: string | null): string {
  const current = normalizeEntityType(type);
  if (!current) return "📍";
  if (isFlight(current, name)) return "✈️";
  switch (current) {
    case "DESTINATION": return "🌍";
    case "PARK": return "🎢";
    case "EVENT": return "🎉";
    case "HOTEL": return "🏨";
    case "SHIP": return "🚢";
    case "AIRPORT": return "🛫";
    case "ATTRACTION": return "🎡";
    case "EXPERIENCE": return "🎟️";
    case "RESTAURANT": return "🍽️";
    case "TRANSPORTATION": return "🚐";
    case "LOCATION": return "📍";
    default: return "📍";
  }
}

const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  DESTINATION: "Destination",
  PARK: "Theme Park",
  EVENT: "Event",
  HOTEL: "Hotel",
  SHIP: "Ship",
  AIRPORT: "Airport",
  ATTRACTION: "Attraction",
  EXPERIENCE: "Experience",
  RESTAURANT: "Food & Drink",
  TRANSPORTATION: "Transport",
  LOCATION: "Location",
};

/** Human-readable label for a type, e.g. "RESTAURANT" → "Food & Drink". */
export function entityTypeLabel(type: EntityType | string): string {
  const current = normalizeEntityType(type);
  return current ? ENTITY_TYPE_LABELS[current] : "Place";
}

interface ActivityTimeLike {
  segmentId?: string | null;
  timezone?: string | null;
  events?: InteractionEvent[];
  createdAt?: DateLike;
  /** Position in the source note, for activities transcribed from one. */
  sortIndex?: number | null;
}

/** Resolves the day a segment covers, for activities that carry no times. */
export type SegmentDayLookup = ((segmentId: string | null | undefined) => Date | null) & {
  /** The segment's last day (noon UTC), when it states one. */
  lastDay?: (segmentId: string | null | undefined) => Date | null;
};

/**
 * Build a `SegmentDayLookup` from the segments already loaded for a trip.
 *
 * Pass the result wherever activity times are derived. Without it those
 * derivations fall back to `createdAt`, which is when the row was written
 * rather than when the thing happened.
 */
export function segmentDayLookup(
  segments: { id: string; startDate?: DateLike; endDate?: DateLike }[],
): SegmentDayLookup {
  const byId = new Map<string, Date>();
  const lastById = new Map<string, Date>();
  for (const s of segments) {
    // Noon UTC of the segment's first calendar day, which every zone in the
    // Americas reads as that same day — however the date was stored.
    const key = dayKeyOf(s.startDate);
    if (key) byId.set(s.id, dateFromDayKey(key));
    const endKey = dayKeyOf(s.endDate);
    if (endKey) lastById.set(s.id, dateFromDayKey(endKey));
  }
  const lookup: SegmentDayLookup = (segmentId) => (segmentId ? (byId.get(segmentId) ?? null) : null);
  lookup.lastDay = (segmentId) => (segmentId ? (lastById.get(segmentId) ?? null) : null);
  return lookup;
}

/** Resolves the zone an activity's times should be read in. */
export type SegmentZoneLookup = (segmentId?: string | null) => Zone;

/**
 * Build a `SegmentZoneLookup` from a trip's segments and its zone.
 *
 * A segment states a zone only when it differs from what encloses it, so the
 * answer is found by walking up: a cruise day inherits the sailing's clock,
 * and the sailing inherits the trip's. The Alaska sailing is why the walk
 * exists — it ran on Pacific out of Vancouver and Alaskan time in port, so a
 * single trip-level zone would misread half of it.
 */
export function segmentZoneLookup(
  segments: { id: string; parentSegmentId?: string | null; timezone?: string | null }[],
  tripZone?: Zone,
): SegmentZoneLookup {
  const byId = new Map(segments.map((s) => [s.id, s]));
  const resolved = new Map<string, Zone>();
  return (segmentId) => {
    if (!segmentId) return tripZone;
    if (resolved.has(segmentId)) return resolved.get(segmentId);
    let cur = byId.get(segmentId);
    const seen = new Set<string>();
    let zone: Zone = tripZone;
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      if (cur.timezone) {
        zone = cur.timezone;
        break;
      }
      cur = cur.parentSegmentId ? byId.get(cur.parentSegmentId) : undefined;
    }
    resolved.set(segmentId, zone);
    return zone;
  };
}

/** The zone-bearing bits of an activity. */
export interface ActivityZoneLike {
  segmentId?: string | null;
  timezone?: string | null;
  events?: { eventTimestamp: { toDate: () => Date }; timezone?: string | null }[];
}

/**
 * The zone an activity happened in: the zone its first event was logged in,
 * else the zone it was created in, else what its segment (then the trip)
 * says. Decides which calendar day the activity belongs to, and is the clock
 * its times are compared against when deciding whether to label one.
 */
export function activityZone(item: ActivityZoneLike, onZone?: SegmentZoneLookup): Zone {
  let first: { ms: number; zone?: string | null } | null = null;
  for (const ev of item.events ?? []) {
    const ms = ev.eventTimestamp?.toDate?.()?.getTime();
    if (ms == null || Number.isNaN(ms)) continue;
    if (!first || ms < first.ms) first = { ms, zone: ev.timezone };
  }
  return first?.zone || item.timezone || onZone?.(item.segmentId) || undefined;
}

/**
 * The zone one event's time reads in: the zone it was logged in, else its
 * activity's.
 */
export function eventZone(
  ev: { timezone?: string | null },
  item: ActivityZoneLike,
  onZone?: SegmentZoneLookup,
): Zone {
  return ev.timezone || item.timezone || onZone?.(item.segmentId) || undefined;
}

/**
 * The zone a step should carry once its time is changed to `newMs`. A step
 * logged late (Boarded Plane tapped after landing in Boston) was stamped with
 * the phone's zone at the moment of the tap; moved back to when it happened,
 * it belongs on the clock of the steps around it — the one just before its new
 * time, else the one just after. A flight is left alone: its own steps cross
 * zones, so the step keeps the zone it has.
 */
export function editedStepZone(
  events: { eventTimestamp: { toMillis(): number }; timezone?: string | null }[],
  edited: { timezone?: string | null },
  newMs: number,
  isFlight = false,
): string | null {
  const own = edited.timezone ?? null;
  if (isFlight) return own;
  let before: { ms: number; zone: string } | null = null;
  let after: { ms: number; zone: string } | null = null;
  for (const e of events) {
    if (e === edited || !e.timezone) continue;
    const ms = e.eventTimestamp.toMillis();
    if (ms <= newMs) {
      if (!before || ms > before.ms) before = { ms, zone: e.timezone };
    } else if (!after || ms < after.ms) {
      after = { ms, zone: e.timezone };
    }
  }
  return before?.zone ?? after?.zone ?? own;
}

/**
 * When an activity happened, for grouping, sorting and metrics.
 *
 * An activity logged live carries its own event times. One transcribed from a
 * written note often carries none — "Skipper Canteen, server Souad" is a
 * complete record of a real visit, and inventing a clock time to make it sort
 * would be worse than leaving it without one. Its *day* is still known: the
 * segment it hangs off is that day.
 *
 * So the segment is the fallback, and `createdAt` only the last resort. Reading
 * `createdAt` first dates a backfilled activity to whenever it was typed in,
 * which for these notes is years after the trip — putting it in the wrong day
 * group, at the wrong end of the sort, and into the wrong bucket for anything
 * counting activity over time.
 */
export function activityStart(
  i: ActivityTimeLike,
  onDay?: SegmentDayLookup,
): Date | null {
  const { start } = getActivityTimeRange(i.events);
  return start ?? createdLiveAt(i, onDay) ?? onDay?.(i.segmentId) ?? i.createdAt?.toDate?.() ?? null;
}

/** Noon UTC of a calendar day is 14 hours either side of that date in any zone. */
const NOON_SLACK_MS = 14 * 60 * 60 * 1000;

/**
 * When a step-less activity was created, if it was created live: set up ahead
 * of its first step (the airport visit started from the plane, waiting for Off
 * Plane), during its segment. It belongs there, not at the top of the
 * segment's first day. A backfilled note (it has a `sortIndex`, or was typed in
 * weeks after) or one planned before its segment starts gets null and keeps
 * the segment's day.
 */
function createdLiveAt(i: ActivityTimeLike, onDay?: SegmentDayLookup): Date | null {
  if (i.sortIndex != null || (i.events && i.events.length > 0)) return null;
  const created = i.createdAt?.toDate?.();
  if (!created) return null;
  const firstDay = onDay?.(i.segmentId);
  const lastDay = onDay?.lastDay?.(i.segmentId);
  if (!firstDay || !lastDay) return null;
  // Created within the segment's dates, read in any zone.
  const ms = created.getTime();
  return ms >= firstDay.getTime() - NOON_SLACK_MS && ms <= lastDay.getTime() + NOON_SLACK_MS ? created : null;
}

/** Latest point of an activity — its last event, else wherever it starts. */
export function activityEnd(i: ActivityTimeLike, onDay?: SegmentDayLookup): Date | null {
  const { end } = getActivityTimeRange(i.events);
  return end ?? activityStart(i, onDay);
}

/**
 * The calendar day an activity belongs to, as `"YYYY-MM-DD"`: the date where
 * it started, on the clock it started on. A flight that boards in Boston on
 * the 15th and lands in California at 11pm is the 15th's, and so is one that
 * lands after midnight Boston time. An activity without times takes its
 * segment's day, and only failing that the day it was created.
 */
export function activityDayKey(
  i: ActivityTimeLike,
  onDay?: SegmentDayLookup,
  onZone?: SegmentZoneLookup,
): string | null {
  const { start } = getActivityTimeRange(i.events);
  const zone = activityZone(i, onZone);
  if (start) return dayKeyInZone(start, zone);
  const live = createdLiveAt(i, onDay);
  if (live) return dayKeyInZone(live, zone);
  const segmentDay = onDay?.(i.segmentId);
  if (segmentDay) return dayKeyOf(segmentDay);
  const created = i.createdAt?.toDate?.();
  return created ? dayKeyInZone(created, zone) : null;
}

/** An activity placed on the timeline, with the key it was placed by. */
export interface OrderedActivity<T> {
  item: T;
  /** The day it belongs to — what the date separator and the row label read. */
  day: Date | null;
  /** That day as `"YYYY-MM-DD"`, read where the activity happened. */
  dayKey: string | null;
  /** The zone it happened in (see `activityZone`). */
  zone: Zone;
  /** Its own first event time, or null when the source recorded no clock. */
  start: Date | null;
  /** Its own last event time, when it spans one. */
  end: Date | null;
  /**
   * What it sorted by. Equal to `start` when the activity has one; otherwise
   * carried forward from the activity before it. Never displayed — it is a
   * position, not a claim about when anything happened.
   */
  sortKey: number;
}

/** Untimed activities are nudged past their anchor by a millisecond apiece,
 *  which orders them without implying any duration. */
const CARRY_STEP_MS = 1;

/**
 * Order a day's activities the way the source recorded them.
 *
 * Times win wherever we have them. The problem is the activities that have
 * none: a note reads "Kilimanjaro Safaris, then Wild Africa Trek" without a
 * clock anywhere, and all a plain time sort can do with those is pile them on
 * the day's midnight, above every timed activity including the morning ones
 * they actually followed.
 *
 * So `sortIndex` — where the activity sat in the note — is walked forward, each
 * untimed activity taking the last real time seen before it. It lands just
 * after the activity it followed in the note, and the moment a real time
 * arrives for it that time takes over and this is ignored. The carry never
 * crosses midnight: an untimed activity opening a day anchors to that day's
 * start rather than inheriting last night's dinner.
 */
export function orderActivities<T extends ActivityTimeLike>(
  activities: T[],
  onDay?: SegmentDayLookup,
  sortDir: "asc" | "desc" = "asc",
  onZone?: SegmentZoneLookup,
): OrderedActivity<T>[] {
  const rows = activities.map((item, idx) => {
    const { start, end } = getActivityTimeRange(item.events);
    const dayKey = activityDayKey(item, onDay, onZone);
    const zone = activityZone(item, onZone);
    return {
      item,
      idx,
      day: activityStart(item, onDay),
      dayKey,
      zone,
      start,
      end,
      // Where it sits on the day: its own time, else when it was created live.
      at: start ?? createdLiveAt(item, onDay),
      // Activities from outside a note carry no index; they sort by their own
      // times, so where they fall in this walk only affects what they anchor.
      noteOrder: item.sortIndex ?? Number.MAX_SAFE_INTEGER,
      sortKey: 0,
    };
  });

  // Carry forward within a day, never across one.
  const byDay = new Map<string, typeof rows>();
  for (const r of rows) {
    // Grouped by the day where each activity happened, so the days here are
    // the days the family lived rather than the ones the reader's device
    // would draw.
    const key = r.dayKey ?? "\u0000undated";
    const bucket = byDay.get(key);
    if (bucket) bucket.push(r);
    else byDay.set(key, [r]);
  }

  for (const bucket of byDay.values()) {
    bucket.sort(
      (a, b) =>
        a.noteOrder - b.noteOrder ||
        (a.at?.getTime() ?? 0) - (b.at?.getTime() ?? 0) ||
        a.idx - b.idx,
    );
    // Anything standing before the first known time goes just ahead of it —
    // or at the day itself, when nothing that day has a time.
    const firstTimed = bucket.reduce<number | null>(
      (min, r) => (r.at && (min == null || r.at.getTime() < min) ? r.at.getTime() : min),
      null,
    );
    let anchor =
      firstTimed != null
        ? firstTimed - (bucket.length + 1) * CARRY_STEP_MS
        : (bucket[0]?.day?.getTime() ?? 0);
    let run = 0;
    for (const r of bucket) {
      if (r.at) {
        r.sortKey = r.at.getTime();
        anchor = r.sortKey;
        run = 0;
      } else {
        run += 1;
        r.sortKey = anchor + run * CARRY_STEP_MS;
      }
    }
  }

  rows.sort((a, b) => a.sortKey - b.sortKey || a.noteOrder - b.noteOrder || a.idx - b.idx);
  if (sortDir === "desc") rows.reverse();
  return rows.map(({ item, day, dayKey, zone, start, end, sortKey }) => ({
    item,
    day,
    dayKey,
    zone,
    start,
    end,
    sortKey,
  }));
}

export function getActivityTimeRange(events: InteractionEvent[] | undefined): { start: Date | null; end: Date | null } {
  if (!events || events.length === 0) return { start: null, end: null };
  let minMs = Infinity;
  let maxMs = -Infinity;
  for (const ev of events) {
    const ms = ev.eventTimestamp.toDate().getTime();
    if (ms < minMs) minMs = ms;
    if (ms > maxMs) maxMs = ms;
  }
  if (minMs === Infinity) return { start: null, end: null };
  return {
    start: new Date(minMs),
    end: maxMs > minMs ? new Date(maxMs) : null,
  };
}

/**
 * Zone a trip's times should be read in.
 *
 * A stored instant is unambiguous; a *rendered* time is not. Formatting with
 * the reader's device clock means a cruise logged off Florida reads three
 * hours early when opened from Seattle — the trip appears to have happened at
 * a time it did not. Passing the trip's own zone keeps a time reading the way
 * it was lived, whoever opens it and from wherever.
 *
 * `undefined` means "the reader's zone", which is what every trip did before
 * this field existed and what an older trip without one still does.
 */
export type Zone = string | undefined;

/** The reader's own zone, for deciding whether a trip's differs from it. */
export function deviceZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/**
 * Short name for a zone at a given instant — "EDT", "AKDT", "GMT+2".
 *
 * Taken at an instant rather than for the zone as a whole because the answer
 * changes across a DST boundary, and a trip can straddle one.
 */
export function zoneAbbrev(date: Date, zone: Zone): string | null {
  if (!zone) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "short" }).formatToParts(date);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? null;
  } catch {
    return null;
  }
}

/**
 * The zone marker to show beside a time, or null when there is nothing worth
 * saying. A reader already in the trip's zone sees times that agree with their
 * own clock, so naming the zone there is noise; a reader outside it sees times
 * that disagree, and the marker is what stops that looking like a bug.
 */
export function zoneNote(date: Date, zone: Zone): string | null {
  if (!zone || zone === deviceZone()) return null;
  return zoneAbbrev(date, zone);
}

/** A wall-clock time like "4:10 PM". Seconds are left out: they're noise when reading a day back. */
export function formatTime(date: Date, zone?: Zone): string {
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: zone,
  });
}

export function formatTimeRange(start: Date | null, end: Date | null, zone?: Zone): string | null {
  if (!start) return null;
  if (!end) return formatTime(start, zone);
  return `${formatTime(start, zone)} – ${formatTime(end, zone)}`;
}

/**
 * A time range whose ends may sit on different clocks — the flight that
 * boards on Eastern and lands on Pacific. When the zones agree it reads like
 * `formatTimeRange`; when they don't, each end is labeled with its own zone
 * so "7:05 PM – 11:02 PM" can't be misread as a four-hour flight.
 */
export function formatZonedTimeRange(
  start: Date | null,
  startZone: Zone,
  end: Date | null,
  endZone: Zone,
): string | null {
  if (!start) return null;
  if (!end || (startZone ?? null) === (endZone ?? null)) return formatTimeRange(start, end, startZone);
  const label = (d: Date, z: Zone) => {
    const abbrev = zoneAbbrev(d, z ?? deviceZone());
    return abbrev ? `${formatTime(d, z)} ${abbrev}` : formatTime(d, z);
  };
  return `${label(start, startZone)} – ${label(end, endZone)}`;
}

/**
 * The zone of an activity's last event, for the far end of its time range.
 */
export function lastEventZone(item: ActivityZoneLike, onZone?: SegmentZoneLookup): Zone {
  let last: { ms: number; ev: { timezone?: string | null } } | null = null;
  for (const ev of item.events ?? []) {
    const ms = ev.eventTimestamp?.toDate?.()?.getTime();
    if (ms == null || Number.isNaN(ms)) continue;
    if (!last || ms > last.ms) last = { ms, ev };
  }
  return last ? eventZone(last.ev, item, onZone) : activityZone(item, onZone);
}

/**
 * A trip's or segment's days, e.g. "Oct 15 — Oct 22".
 *
 * These are calendar days rather than moments, so no zone applies: the 15th
 * is the 15th whether it is read in Boston or Anaheim.
 */
export function formatSegmentDateRange(startDate: DateLike, endDate: DateLike): string {
  const start = dayKeyOf(startDate);
  const end = dayKeyOf(endDate);
  if (!start || !end) return "";
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const startStr = formatDayKey(start, opts);
  if (start === end) return startStr;
  return `${startStr} — ${formatDayKey(end, opts)}`;
}

/** How many calendar days a trip or segment covers, both ends included. */
export function segmentDayCount(startDate: DateLike, endDate: DateLike): number {
  const start = dayKeyOf(startDate);
  const end = dayKeyOf(endDate);
  if (!start || !end) return 0;
  return daysBetweenKeys(start, end) + 1;
}

export function formatEventType(eventType: string): string {
  return eventType
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** The last event logged on an activity, by time. */
function latestEvent(events: InteractionEvent[] | undefined): InteractionEvent | null {
  if (!events || events.length === 0) return null;
  let latest: InteractionEvent | null = null;
  let latestMs = -Infinity;
  for (const ev of events) {
    const ms = ev.eventTimestamp.toDate().getTime();
    if (ms > latestMs) {
      latestMs = ms;
      latest = ev;
    }
  }
  return latest;
}

/**
 * Nobody has closed this activity out: nothing in the END category has been
 * logged on it. An activity with no events at all counts as open — a park you
 * just checked into hasn't ended either, it simply hasn't been timed yet.
 *
 * This is the "could I still be here?" test, without the clock: it says a hotel
 * stay logged yesterday morning is still running, where `isActivityActive`
 * (which also wants a recent event) would not.
 */
export function isActivityOpen(events: InteractionEvent[] | undefined): boolean {
  const latest = latestEvent(events);
  if (!latest) return true;
  return EVENT_TYPE_CATEGORY_MAP[latest.eventType] !== "END";
}

/**
 * Places you stay at rather than things you do: a hotel stay, a park day, a
 * cruise. They run until you log leaving, however quiet the hours in between —
 * a hotel checked into this morning is still where you're sleeping tonight.
 */
export const STAY_ENTITY_TYPES: ReadonlySet<EntityType> = new Set<EntityType>([
  "HOTEL",
  "PARK",
  "EVENT",
  "SHIP",
  "DESTINATION",
  "LOCATION",
]);

/** How long a quiet activity stays active: 12 hours since its last event. */
export const ACTIVE_WINDOW_MS = 12 * 60 * 60 * 1000;
/** A stay with a forgotten "Left" stops showing as active after two weeks. */
export const STAY_ACTIVE_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Returns true if the activity has events, the most recent event is not in
 * the END category, and that event was recent: within 12 hours, or for a stay
 * (see `STAY_ENTITY_TYPES`) within two weeks, so it lasts until "Left".
 *
 * A flight (`customResponses` with a flight number or route) is the exception:
 * it's still live after "Landed", while you taxi and wait to get off, and ends
 * at "Off Plane" (or any other END step after it).
 */
export function isActivityActive(
  events: InteractionEvent[] | undefined,
  entityType?: EntityType | string | null,
  customResponses?: Record<string, unknown> | null,
): boolean {
  const latest = latestEvent(events);
  if (!latest) return false;
  if (EVENT_TYPE_CATEGORY_MAP[latest.eventType] === "END") {
    if (!(latest.eventType === "LANDED" && isFlightActivity(customResponses))) return false;
  }
  const type = normalizeEntityType(entityType);
  const window = type && STAY_ENTITY_TYPES.has(type) ? STAY_ACTIVE_WINDOW_MS : ACTIVE_WINDOW_MS;
  return Date.now() - latest.eventTimestamp.toDate().getTime() < window;
}

// Dining wait stops as soon as you're seated or place your order — whichever
// comes first. One rule covers table service (Seated is logged first) and quick
// service (no Seated, so Order Placed is the first stop). Shows, which also log
// Seated, likewise count the wait as ending when you're seated.
const DINING_WAIT_STOP_TYPES: EventType[] = ["SEATED", "ORDER_PLACED"];
// For everything else the wait ends when the experience becomes active.
const ACTIVE_WAIT_STOP_TYPES: EventType[] = ["WAIT_END", "MAIN_EVENT_START"];

/**
 * Calculates total wait time in seconds for an interaction.
 * Wait = first WAIT_START → the first SEATED or ORDER_PLACED if either was
 * logged (dining/shows), otherwise the last WAIT_END or MAIN_EVENT_START.
 */
export function calcTotalWaitTime(events: InteractionEvent[] | undefined): number {
  if (!events || events.length === 0) return 0;
  const sorted = [...events].sort(
    (a, b) => a.eventTimestamp.toDate().getTime() - b.eventTimestamp.toDate().getTime()
  );

  const firstWaitStart = sorted.find((e) => e.eventType === "WAIT_START");
  if (!firstWaitStart) return 0;
  const startMs = firstWaitStart.eventTimestamp.toDate().getTime();

  // Prefer the first seated/ordered milestone at or after the wait started.
  let waitEnd = sorted.find(
    (e) =>
      DINING_WAIT_STOP_TYPES.includes(e.eventType) &&
      e.eventTimestamp.toDate().getTime() >= startMs
  );

  // Otherwise fall back to the last point the experience became active.
  if (!waitEnd) {
    for (let i = sorted.length - 1; i >= 0; i--) {
      const ev = sorted[i];
      if (ev && ACTIVE_WAIT_STOP_TYPES.includes(ev.eventType)) {
        waitEnd = ev;
        break;
      }
    }
  }

  if (!waitEnd) return 0;
  const diff = (waitEnd.eventTimestamp.toDate().getTime() - startMs) / 1000;
  return diff > 0 ? diff : 0;
}

/**
 * Secondary dining metric: time in seconds from placing an order to receiving
 * it (first ORDER_PLACED → first following ORDER_RECEIVED, or the PICKED_UP
 * that ends a take-away order). 0 unless both events are present.
 */
export function calcOrderToReceivedTime(events: InteractionEvent[] | undefined): number {
  if (!events || events.length === 0) return 0;
  const sorted = [...events].sort(
    (a, b) => a.eventTimestamp.toDate().getTime() - b.eventTimestamp.toDate().getTime()
  );

  const placed = sorted.find((e) => e.eventType === "ORDER_PLACED");
  if (!placed) return 0;
  const placedMs = placed.eventTimestamp.toDate().getTime();

  const received = sorted.find(
    (e) =>
      (e.eventType === "ORDER_RECEIVED" || e.eventType === "PICKED_UP") &&
      e.eventTimestamp.toDate().getTime() >= placedMs
  );
  if (!received) return 0;

  const diff = (received.eventTimestamp.toDate().getTime() - placedMs) / 1000;
  return diff > 0 ? diff : 0;
}

/**
 * Calculates total delay time in seconds for an interaction.
 * Sums all DELAY_START → DELAY_END/MAIN_EVENT_START/WAIT_END pairs.
 * Mirrors ThemeParkVacations backend logic.
 */
export function calcTotalDelayTime(events: InteractionEvent[] | undefined): number {
  if (!events || events.length === 0) return 0;
  const sorted = [...events].sort(
    (a, b) => a.eventTimestamp.toDate().getTime() - b.eventTimestamp.toDate().getTime()
  );
  let total = 0;
  let i = 0;
  while (i < sorted.length) {
    const current = sorted[i]!;
    if (current.eventType === "DELAY_START") {
      for (let j = i + 1; j < sorted.length; j++) {
        const next = sorted[j]!;
        if (
          next.eventType === "DELAY_END" ||
          next.eventType === "MAIN_EVENT_START" ||
          next.eventType === "WAIT_END"
        ) {
          const delay =
            (next.eventTimestamp.toDate().getTime() -
              current.eventTimestamp.toDate().getTime()) /
            1000;
          if (delay > 0) total += delay;
          i = j;
          break;
        }
      }
    }
    i++;
  }
  return total;
}

/**
 * Per-round dining metric: time in seconds a single order round took, from its
 * own placedAt to receivedAt. 0 unless both timestamps are set. Used when an
 * interaction tracks orders as rounds (lounges).
 */
export function calcRoundDuration(round: LoungeRound | undefined | null): number {
  if (!round?.placedAt || !round?.receivedAt) return 0;
  const diff = (round.receivedAt.toDate().getTime() - round.placedAt.toDate().getTime()) / 1000;
  return diff > 0 ? diff : 0;
}

/**
 * Project dining rounds' placed/received timestamps into timeline events so
 * they show in the timeline and feed the wait-time / order metrics. Existing
 * round-derived events (roundIndex set) are fully regenerated; every other
 * event is preserved. Rounds are the source of truth — these mirrors are
 * rendered read-only in the timeline.
 */
export function projectRoundEvents(
  events: InteractionEvent[] | undefined,
  rounds: LoungeRound[] | undefined,
): InteractionEvent[] {
  const kept = (events ?? []).filter((e) => e.roundIndex == null);
  const mirrored: InteractionEvent[] = [];
  (rounds ?? []).forEach((round, i) => {
    if (round.placedAt) {
      mirrored.push({
        eventType: "ORDER_PLACED",
        eventTimestamp: round.placedAt,
        notes: null,
        customLabel: `Round ${i + 1} Placed`,
        roundIndex: i,
      });
    }
    if (round.receivedAt) {
      mirrored.push({
        eventType: "ORDER_RECEIVED",
        eventTimestamp: round.receivedAt,
        notes: null,
        customLabel: `Round ${i + 1} Received`,
        roundIndex: i,
      });
    }
  });
  return [...kept, ...mirrored];
}

/**
 * Timeline events for an interaction with any dining-round timing projected in
 * (read-only). Use this anywhere the timeline is shown or wait/order metrics are
 * computed, so rounds appear and feed the metrics without persisting a duplicate
 * copy of the timestamps. `rounds` may be passed explicitly (e.g. live editor
 * state) or read from customResponses.
 */
export function interactionTimelineEvents(
  events: InteractionEvent[] | undefined,
  rounds: LoungeRound[] | null | undefined,
): InteractionEvent[] {
  if (!rounds || rounds.length === 0) return events ?? [];
  return projectRoundEvents(events, rounds);
}

/** Format seconds as Xm or Xh Ym for display */
/** A running clock: "38:12" under an hour, "1:04:09" over. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export function formatDuration(seconds: number): string {
  if (seconds <= 0) return "0m";
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem > 0 ? `${hrs}h ${rem}m` : `${hrs}h`;
}

/**
 * Universal Express (and similar skip-the-line / priority passes) are marketed
 * with an expected wait of roughly half the posted standby wait. Given an
 * access-tier display name, returns that multiplier, or null when the tier
 * isn't an express-style pass. Disney's Lightning Lane is intentionally left
 * out — its expectation isn't a simple 50% of standby.
 */
export const EXPRESS_WAIT_FACTOR = 0.5;
const EXPRESS_TIER_RE = /\b(express|priority|skip[\s-]?the[\s-]?line|fast[\s-]?pass)\b/i;
export function expressWaitFactor(tierName: string | null | undefined): number | null {
  if (!tierName) return null;
  return EXPRESS_TIER_RE.test(tierName) ? EXPRESS_WAIT_FACTOR : null;
}

/** Expected express wait (rounded minutes) from a posted standby wait, or null. */
export function expectedExpressWait(
  postedWaitMinutes: number | null | undefined,
  tierName: string | null | undefined,
): number | null {
  const factor = expressWaitFactor(tierName);
  if (factor == null || postedWaitMinutes == null || postedWaitMinutes <= 0) return null;
  return Math.round(postedWaitMinutes * factor);
}

interface SegLike {
  id: string;
  parentSegmentId?: string | null;
  startDate?: DateLike;
}

/**
 * Most recent activity time (ms) within each segment's subtree (the segment
 * plus all its descendant segments); 0 when the subtree has no logged activity.
 * Activity time = last logged event, else its start, else when it was created —
 * the same basis the activity list uses.
 */
export function segmentSubtreeActivityMs(
  segments: SegLike[],
  interactions: ActivityTimeLike[],
): Map<string, number> {
  const onDay = segmentDayLookup(segments);
  const directMax = new Map<string, number>();
  for (const i of interactions) {
    if (!i.segmentId) continue;
    const d = activityEnd(i, onDay);
    const ms = d ? d.getTime() : 0;
    if (ms > (directMax.get(i.segmentId) ?? 0)) directMax.set(i.segmentId, ms);
  }

  const kids = new Map<string, string[]>();
  for (const s of segments) {
    if (!s.parentSegmentId) continue;
    const arr = kids.get(s.parentSegmentId);
    if (arr) arr.push(s.id);
    else kids.set(s.parentSegmentId, [s.id]);
  }

  const memo = new Map<string, number>();
  const visiting = new Set<string>();
  const subtree = (id: string): number => {
    const cached = memo.get(id);
    if (cached !== undefined) return cached;
    if (visiting.has(id)) return directMax.get(id) ?? 0; // guard against cycles
    visiting.add(id);
    let max = directMax.get(id) ?? 0;
    for (const c of kids.get(id) ?? []) max = Math.max(max, subtree(c));
    visiting.delete(id);
    memo.set(id, max);
    return max;
  };

  const out = new Map<string, number>();
  for (const s of segments) out.set(s.id, subtree(s.id));
  return out;
}

/**
 * Order segments newest-first by their subtree's most recent activity, so the
 * segment you most recently logged in floats to the top. When two segments have
 * the same activity recency (or neither has anything logged — e.g. same start
 * day), the one that ends later / is still ongoing wins the tiebreak via
 * `fallbackMsOf` (pass the segment's end date). A logged segment always ranks
 * above one with nothing logged.
 */
export function orderSegmentsNewestFirst<T extends { id: string }>(
  segs: T[],
  activityMs: Map<string, number>,
  fallbackMsOf: (s: T) => number,
): T[] {
  return [...segs].sort((a, b) => {
    const ka = activityMs.get(a.id) ?? 0;
    const kb = activityMs.get(b.id) ?? 0;
    if (ka !== kb) return kb - ka; // most recent activity first; unlogged (0) below logged
    return fallbackMsOf(b) - fallbackMsOf(a); // tie / both unlogged: later-ending (ongoing) first
  });
}

interface ChildSegLike {
  id: string;
  dayNumber?: number | null;
  dayType?: CruiseDayType | null;
  startDate?: DateLike;
  endDate?: DateLike;
}

/**
 * Order a segment's children for display. A cruise's itinerary days are
 * numbered, so they always read forward — Day 1, Day 2, … — however the
 * itinerary is sorted: flipping them puts Day 7 above Day 1 and leaves the
 * numbering fighting the order. Everything else follows `sortDir`, where
 * newest-first floats the sub-segment you last logged in to the top.
 */
export function orderChildSegments<T extends ChildSegLike>(
  kids: T[],
  sortDir: "asc" | "desc",
  activityMs?: Map<string, number>,
): T[] {
  const days = kids
    .filter((s) => isCruiseDay(s))
    .sort((a, b) => (a.dayNumber ?? 0) - (b.dayNumber ?? 0));
  const rest = kids.filter((s) => !isCruiseDay(s));
  if (sortDir !== "desc") return [...days, ...rest];
  const restOrdered = activityMs
    ? orderSegmentsNewestFirst(
        rest,
        activityMs,
        (s) => s.endDate?.toDate?.()?.getTime() ?? s.startDate?.toDate?.()?.getTime() ?? 0,
      )
    : [...rest].reverse();
  return [...days, ...restOrdered];
}

/**
 * Whether today — the calendar day where this device is right now — falls
 * within a trip's or segment's days.
 */
export function isSegmentCurrent(startDate: DateLike, endDate: DateLike): boolean {
  return dayRangeContains(startDate, endDate);
}

const dateKeyOpts: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric" };

/**
 * Which calendar day an instant falls on, as a groupable key.
 *
 * Read in the trip's zone, so a 10pm dinner in Florida groups under that
 * evening rather than sliding into the next day for a reader further east.
 */
export function dateBucketKey(d: Date, zone?: Zone): string {
  return dayKeyInZone(d, zone);
}

export function formatDateLabel(d: Date, zone?: Zone): string {
  return d.toLocaleDateString(undefined, zone ? { ...dateKeyOpts, timeZone: zone } : dateKeyOpts);
}


/** Midnight (local time) of the given date, as epoch milliseconds. */
export function startOfDayMs(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export type TripStatusTone = "upcoming" | "now" | "past";

/**
 * Human-friendly status for a date range, e.g. "Tomorrow", "3 days away",
 * "Day 2 of 7", "Today", "Wrapped up". Day counting is inclusive and uses
 * local calendar days.
 */
export function tripStatusLabel(
  startDate: DateLike,
  endDate: DateLike,
): { label: string; tone: TripStatusTone } {
  const s = dayKeyOf(startDate);
  const e = dayKeyOf(endDate);
  if (!s || !e) return { label: "", tone: "upcoming" };
  const today = todayKey();
  if (today < s) {
    const days = daysBetweenKeys(today, s);
    return { label: days <= 1 ? "Tomorrow" : `${days} days away`, tone: "upcoming" };
  }
  if (today > e) return { label: "Wrapped up", tone: "past" };
  const totalDays = daysBetweenKeys(s, e) + 1;
  const dayNum = daysBetweenKeys(s, today) + 1;
  return { label: totalDays > 1 ? `Day ${dayNum} of ${totalDays}` : "Today", tone: "now" };
}

interface SegmentPickLike {
  id: string;
  startDate: DateLike;
  endDate: DateLike;
  parentSegmentId?: string | null;
  createdAt?: DateLike;
}

const msOf = (d: DateLike): number => d?.toDate?.()?.getTime() ?? 0;

/**
 * The segment you are actually in right now — the one new activities file
 * under by default, and the one tagged "NOW".
 *
 * More than one segment can cover today. The morning you fly out, "Boston →
 * LAX" and "Disneyland" both start today; the day you leave the ship, its last
 * day and Universal both do. Dates alone can't say which half of the day you
 * are in, but where the family last logged something can:
 *
 *   1. Stay put. If the most recent activity on the trip (`lastSegmentId`, from
 *      `lastLoggedSegmentId`) sits in a segment that still covers today, keep
 *      filing there — descending into a covering child, so a cruise's day
 *      wins over the sailing someone happened to file on. Moving on is one
 *      manual pick in the log sheet, and everything after follows it.
 *   2. Otherwise, the segment that ends soonest: on a travel day that is the
 *      leg you are on before the destination that runs all week.
 *   3. Then deepest — a cruise's day beats the cruise that holds it.
 *   4. Then latest start, then most recently created.
 *
 * The watch app implements the same rule (`SegmentRepository` in
 * TripTrackerWearOS); keep the two in step.
 */
export function findCurrentSegmentId(
  segments: SegmentPickLike[],
  lastSegmentId?: string | null,
): string | null {
  const today = todayKey();
  const current = segments.filter((s) => dayRangeContains(s.startDate, s.endDate, today));
  if (current.length === 0) return null;
  const byId = new Map(segments.map((s) => [s.id, s] as const));
  const depth = (s: SegmentPickLike): number => {
    let d = 0;
    let cur: SegmentPickLike | undefined = s;
    const seen = new Set<string>();
    while (cur?.parentSegmentId && !seen.has(cur.id)) {
      seen.add(cur.id);
      const parent = byId.get(cur.parentSegmentId);
      if (!parent) break;
      d++;
      cur = parent;
    }
    return d;
  };
  const rank = (list: SegmentPickLike[]): SegmentPickLike =>
    [...list].sort((a, b) => {
      const ae = dayKeyOf(a.endDate) ?? "";
      const be = dayKeyOf(b.endDate) ?? "";
      if (ae !== be) return ae < be ? -1 : 1;
      const as = dayKeyOf(a.startDate) ?? "";
      const bs = dayKeyOf(b.startDate) ?? "";
      return (
        depth(b) - depth(a) ||
        (as === bs ? 0 : as < bs ? 1 : -1) ||
        msOf(b.createdAt) - msOf(a.createdAt) ||
        (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
      );
    })[0]!;

  let picked = lastSegmentId ? current.find((s) => s.id === lastSegmentId) : undefined;
  if (picked) {
    const seen = new Set<string>();
    for (;;) {
      seen.add(picked.id);
      const parentId: string = picked.id;
      const children = current.filter((s) => s.parentSegmentId === parentId && !seen.has(s.id));
      if (children.length === 0) break;
      picked = rank(children);
    }
    return picked.id;
  }
  return rank(current).id;
}

/**
 * The segment of the most recently created activity — the "where was the
 * family last logging" signal `findCurrentSegmentId` stays with. Undefined
 * when nothing has been logged yet.
 */
export function lastLoggedSegmentId(
  interactions: { segmentId?: string | null; createdAt?: DateLike }[],
): string | null | undefined {
  let best: { ms: number; segmentId: string | null } | undefined;
  for (const i of interactions) {
    const ms = msOf(i.createdAt);
    if (!best || ms > best.ms) best = { ms, segmentId: i.segmentId ?? null };
  }
  return best?.segmentId;
}

/**
 * The current segment plus its ancestors — everything that should read as
 * happening "now". A segment that merely covers today but has been superseded
 * (the park you left this morning) is deliberately not in here.
 */
export function activeSegmentChainIds(
  segments: SegmentPickLike[],
  lastSegmentId?: string | null,
): Set<string> {
  const activeId = findCurrentSegmentId(segments, lastSegmentId);
  const chain = new Set<string>();
  if (!activeId) return chain;
  const byId = new Map(segments.map((s) => [s.id, s] as const));
  let cur: SegmentPickLike | undefined = byId.get(activeId);
  while (cur && !chain.has(cur.id)) {
    chain.add(cur.id);
    cur = cur.parentSegmentId ? byId.get(cur.parentSegmentId) : undefined;
  }
  return chain;
}

/** Distinct entity ids of the most recent activities BY TIME (last event →
 *  start → created), newest first, capped at `limit`. Used to seed the
 *  "At" / recent-places suggestions in the log flow. */
export function recentEntityIdsByTime(
  interactions: (ActivityTimeLike & { entityId?: string | null })[],
  limit = 5,
  onDay?: SegmentDayLookup,
): string[] {
  const scored = interactions
    .filter((i): i is typeof i & { entityId: string } => !!i.entityId)
    .map((i) => {
      const when = activityEnd(i, onDay);
      return { entityId: i.entityId, t: when ? when.getTime() : 0 };
    })
    .sort((a, b) => b.t - a.t);
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const s of scored) {
    if (seen.has(s.entityId)) continue;
    seen.add(s.entityId);
    ids.push(s.entityId);
    if (ids.length >= limit) break;
  }
  return ids;
}

/** Seed entities for the log-sheet "At" suggestions, scoped to a segment:
 *  the most recent activities logged under that segment (or its child days),
 *  followed by the segment's own primary entity and its ancestor segments'
 *  entities. Falls back to trip-wide recents when no segment is given. */
export function segmentLogSeeds(
  segments: {
    id: string;
    entityId?: string | null;
    parentSegmentId?: string | null;
    startDate?: DateLike;
    endDate?: DateLike;
  }[],
  interactions: { segmentId?: string | null; entityId?: string | null; events?: InteractionEvent[]; createdAt?: DateLike }[],
  segmentId: string | null,
  limit = 4,
): string[] {
  const seeds: string[] = [];
  if (segmentId) {
    // Scope = the segment plus all its descendants (e.g. cruise days).
    const childrenByParent = new Map<string, string[]>();
    for (const s of segments) {
      if (s.parentSegmentId) {
        const arr = childrenByParent.get(s.parentSegmentId) ?? [];
        arr.push(s.id);
        childrenByParent.set(s.parentSegmentId, arr);
      }
    }
    const scope = new Set<string>();
    const stack = [segmentId];
    while (stack.length) {
      const id = stack.pop()!;
      if (scope.has(id)) continue;
      scope.add(id);
      for (const c of childrenByParent.get(id) ?? []) stack.push(c);
    }
    seeds.push(
      ...recentEntityIdsByTime(
        interactions.filter((i) => i.segmentId != null && scope.has(i.segmentId)),
        limit,
        segmentDayLookup(segments),
      ),
    );
    // The segment's own entity, then up the segment-parent chain.
    const byId = new Map(segments.map((s) => [s.id, s] as const));
    const guard = new Set<string>();
    let cur = byId.get(segmentId);
    while (cur && !guard.has(cur.id)) {
      guard.add(cur.id);
      if (cur.entityId) seeds.push(cur.entityId);
      cur = cur.parentSegmentId ? byId.get(cur.parentSegmentId) : undefined;
    }
  } else {
    seeds.push(...recentEntityIdsByTime(interactions, limit, segmentDayLookup(segments)));
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of seeds) {
    if (id && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}
